// FILE: src/pages/ManageTillUserPage.jsx
//
// Manage Till Users — UI redesign.
//
// Goals of the redesign:
//   - Geist / Geist Mono typography
//   - Scoped CSS under `.mtpg-root` (so it can't bleed into other pages)
//   - Sticky page-top (title + Create button + search bar)
//   - Compact table (name / description / created) inside a white card
//   - Create & Edit dialogs re-skinned but powered by MUI Dialog underneath
//
// IMPORTANT — what we preserved from the previous file:
//   - Server pagination contract: GET /tilluser with { search, page, limit }
//     (page is 1-indexed), reading res.data.tillUsers / res.data.total.
//   - Create (POST /tilluser), Edit (PUT /tilluser/:id), Delete
//     (DELETE /tilluser/:id) with the exact same payloads and messages.
//   - window.confirm() guard on delete — kept as-is (behavioral).
//   - MyPaginationComponent with its exact props contract.
//   - All validation ("name required") and loading flags.
//   - Feedback state (alertMessage/alertSeverity) unchanged — only its
//     presentation moved from an inline Alert to the bottom-center Snackbar.

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import API from "../api";
import { formatDate } from "../utils/formatDate";
import MyPaginationComponent from "../components/MyPaginationComponent"; // Custom pagination

// ----------------------------------------
// CSS — fully scoped under `.mtpg-root`.
// ----------------------------------------
const MANAGE_TILL_USER_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Geist:wght@400;450;500;600;700&family=Geist+Mono:wght@400;500;600&display=swap');

/* Dialog papers reuse .mtpg-root for tokens — never inherit page sizing */
.MuiPaper-root.mtpg-root {
  min-height: 0;
  background: #fff;
}
.mtpg-root {
  --mtpg-primary: #1E90FF;
  --mtpg-primary-soft: #E8F3FF;
  --mtpg-warn: #FFC107;
  --mtpg-warn-soft: #FFF7DC;
  --mtpg-warn-deep: #B86E00;
  --mtpg-success: #2E7D32;
  --mtpg-success-soft: #E8F5E9;
  --mtpg-danger: #C62828;
  --mtpg-danger-soft: #FDECEC;
  --mtpg-text-1: #111827;
  --mtpg-text-2: #374151;
  --mtpg-text-3: #6B7280;
  --mtpg-text-4: #9CA3AF;
  --mtpg-border-1: #ECECEC;
  --mtpg-border-2: #DDDDDD;
  --mtpg-surface: #FFFFFF;
  --mtpg-surface-sub: #FAFAFA;
  --mtpg-surface-sub-2: #F4F4F5;
  --mtpg-radius-sm: 6px;
  --mtpg-radius-md: 8px;
  --mtpg-radius-lg: 12px;
  --mtpg-shadow-sm: 0 1px 2px rgba(15, 23, 42, 0.04);
  --mtpg-shadow-md: 0 4px 16px rgba(15, 23, 42, 0.06);
  --mtpg-navbar-h: 60px;

  font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-feature-settings: 'cv11', 'ss03';
  color: var(--mtpg-text-1);
  background: var(--mtpg-surface-sub);
  min-height: 100vh;
  padding: 0;
  font-size: 13.5px;
  line-height: 1.5;
  letter-spacing: -0.005em;
}
.mtpg-root * { box-sizing: border-box; }

/* ----- Sticky top region (page header + search bar) ----- */
.mtpg-sticky-top {
  position: sticky;
  top: var(--mtpg-navbar-h);
  z-index: 30;
  background: var(--mtpg-surface-sub);
  border-bottom: 1px solid var(--mtpg-border-1);
}
.mtpg-header {
  padding: 20px 24px 12px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
}
.mtpg-title-block { min-width: 0; flex: 1 1 320px; }
.mtpg-title {
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 0 0 4px;
  color: var(--mtpg-text-1);
}
.mtpg-subtitle {
  display: flex; align-items: center; gap: 10px;
  font-size: 12.5px; color: var(--mtpg-text-3); flex-wrap: wrap;
}
.mtpg-subtitle-dot { width: 4px; height: 4px; border-radius: 50%; background: var(--mtpg-text-4); }
.mtpg-subtitle-count {
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 11.5px;
  color: var(--mtpg-text-3);
}
.mtpg-subtitle-count b { font-weight: 600; color: var(--mtpg-text-2); }

/* ----- Action bar ----- */
.mtpg-actions {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
}
.mtpg-btn {
  display: inline-flex; align-items: center; gap: 7px;
  height: 34px; padding: 0 14px;
  font-family: inherit; font-size: 12.5px; font-weight: 500;
  border-radius: var(--mtpg-radius-md);
  border: 1px solid transparent; cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.mtpg-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.mtpg-btn-primary { background: var(--mtpg-primary); color: #fff; }
.mtpg-btn-primary:hover:not(:disabled) { background: #1A7DD9; }
.mtpg-btn-ghost {
  background: var(--mtpg-surface); color: var(--mtpg-text-2);
  border-color: var(--mtpg-border-2);
}
.mtpg-btn-ghost:hover:not(:disabled) { background: var(--mtpg-surface-sub-2); }

/* ----- Search / filter bar ----- */
.mtpg-filterbar {
  display: flex; align-items: flex-end; gap: 10px; flex-wrap: wrap;
  padding: 14px 24px 16px;
  background: var(--mtpg-surface);
  border-top: 1px solid var(--mtpg-border-1);
}
.mtpg-filter-field { display: flex; flex-direction: column; gap: 4px; }
.mtpg-filter-field label {
  font-size: 10.5px; font-weight: 500; text-transform: uppercase;
  letter-spacing: 0.04em; color: var(--mtpg-text-3);
}
.mtpg-input {
  height: 34px; padding: 0 10px;
  background: var(--mtpg-surface);
  border: 1px solid var(--mtpg-border-2);
  border-radius: var(--mtpg-radius-md);
  font-family: inherit; font-size: 13px; color: var(--mtpg-text-1);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  min-width: 140px;
}
.mtpg-input:focus {
  outline: none;
  border-color: var(--mtpg-primary);
  box-shadow: 0 0 0 3px var(--mtpg-primary-soft);
}
.mtpg-input-search {
  background-image: url("data:image/svg+xml,%3Csvg width='14' height='14' viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg' fill='none'%3E%3Ccircle cx='7' cy='7' r='4.5' stroke='%236B7280' stroke-width='1.4'/%3E%3Cpath d='M10.5 10.5l3 3' stroke='%236B7280' stroke-width='1.4' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: 10px center;
  padding-left: 30px;
  min-width: 280px;
}
.mtpg-filter-meta {
  margin-left: auto;
  align-self: center;
  font-size: 11.5px; color: var(--mtpg-text-3);
  display: inline-flex; align-items: center; gap: 5px;
}
.mtpg-filter-meta b {
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-weight: 600; color: var(--mtpg-text-2);
}

/* ----- Body region ----- */
.mtpg-body { padding: 18px 24px 32px; }

/* ----- Table card ----- */
.mtpg-card {
  background: var(--mtpg-surface);
  border: 1px solid var(--mtpg-border-1);
  border-radius: var(--mtpg-radius-lg);
  box-shadow: var(--mtpg-shadow-sm);
  overflow: hidden;
}
.mtpg-table-wrap { width: 100%; overflow-x: auto; }
.mtpg-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  min-width: 640px;
}
.mtpg-table thead th {
  background: var(--mtpg-surface);
  color: var(--mtpg-text-2);
  font-size: 11.5px; font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 10px 12px;
  border-bottom: 1px solid var(--mtpg-border-2);
  text-align: left;
  white-space: nowrap;
}
.mtpg-table thead th.r { text-align: right; }
.mtpg-table tbody td {
  padding: 11px 12px;
  border-bottom: 1px solid var(--mtpg-border-1);
  vertical-align: middle;
  color: var(--mtpg-text-1);
}
.mtpg-table tbody tr:last-child td { border-bottom: 0; }
.mtpg-table tbody tr { transition: background 0.1s ease; }
.mtpg-table tbody tr:hover { background: var(--mtpg-surface-sub); }
.mtpg-table tbody td.mono {
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 12.5px;
  color: var(--mtpg-text-2);
  white-space: nowrap;
}
.mtpg-name-cell {
  display: flex; align-items: center; gap: 10px; min-width: 160px;
}
.mtpg-name-avatar {
  width: 26px; height: 26px; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 50%;
  background: var(--mtpg-primary-soft);
  color: var(--mtpg-primary);
  font-size: 11.5px; font-weight: 600;
  text-transform: uppercase;
}
.mtpg-name-text { font-weight: 500; color: var(--mtpg-text-1); font-size: 13px; }
.mtpg-desc { color: var(--mtpg-text-2); font-size: 12.5px; }
.mtpg-desc-empty { color: var(--mtpg-text-4); font-style: italic; }

/* Row actions */
.mtpg-row-actions {
  display: flex; justify-content: flex-end; align-items: center; gap: 6px;
}
.mtpg-icon-btn {
  width: 28px; height: 28px;
  display: inline-flex; align-items: center; justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--mtpg-radius-sm);
  color: var(--mtpg-text-3);
  cursor: pointer;
  transition: all 0.12s ease;
}
.mtpg-icon-btn:hover {
  background: var(--mtpg-surface-sub-2);
  color: var(--mtpg-text-1);
  border-color: var(--mtpg-border-2);
}
.mtpg-icon-btn-danger:hover {
  background: var(--mtpg-danger-soft);
  color: var(--mtpg-danger);
  border-color: #F4C7C7;
}

/* ----- Empty / Loading states ----- */
.mtpg-state {
  padding: 56px 32px;
  text-align: center;
  color: var(--mtpg-text-3);
}
.mtpg-state-icon {
  margin: 0 auto 12px;
  width: 44px; height: 44px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 50%;
  background: var(--mtpg-surface-sub-2);
  color: var(--mtpg-text-3);
}
.mtpg-state-title { color: var(--mtpg-text-2); font-size: 14px; font-weight: 500; margin-bottom: 4px; }
.mtpg-state-sub { font-size: 12.5px; color: var(--mtpg-text-3); max-width: 360px; margin: 0 auto; }
.mtpg-state-loading {
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  padding: 64px 32px;
  color: var(--mtpg-text-3); font-size: 12.5px;
}

/* ----- Pagination wrapper (card footer) ----- */
.mtpg-pagination-wrap {
  border-top: 1px solid var(--mtpg-border-1);
  background: var(--mtpg-surface);
}

/* ----- Modal skinning (MUI Dialog underneath, custom look) ----- */
.mtpg-dlg .MuiPaper-root {
  font-family: 'Geist', -apple-system, BlinkMacSystemFont, sans-serif !important;
  border-radius: var(--mtpg-radius-lg) !important;
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.18) !important;
  overflow: hidden;
}
.mtpg-dlg-head {
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--mtpg-border-1);
}
.mtpg-dlg-title { font-size: 16px; font-weight: 600; color: var(--mtpg-text-1); margin: 0; }
.mtpg-dlg-sub { font-size: 12px; color: var(--mtpg-text-3); margin-top: 3px; }
.mtpg-dlg-close {
  background: none; border: none; color: var(--mtpg-text-3);
  width: 28px; height: 28px; border-radius: 6px; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
}
.mtpg-dlg-close:hover { background: var(--mtpg-surface-sub-2); color: var(--mtpg-text-1); }
.mtpg-dlg-body { padding: 18px 20px; }
.mtpg-dlg-foot {
  padding: 14px 20px;
  border-top: 1px solid var(--mtpg-border-1);
  background: var(--mtpg-surface-sub);
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px;
}
.mtpg-dlg-foot-l { font-size: 12px; color: var(--mtpg-text-3); }
.mtpg-dlg-foot-r { display: flex; gap: 8px; }

/* Form field bits inside dialogs */
.mtpg-flab {
  display: block; font-size: 11px; font-weight: 500;
  color: var(--mtpg-text-2); letter-spacing: 0.02em;
  text-transform: uppercase; margin-bottom: 4px;
}
.mtpg-flab-req::after { content: ' *'; color: var(--mtpg-primary); font-weight: 700; }
.mtpg-fhint { font-size: 11px; color: var(--mtpg-text-3); margin-top: 3px; }
.mtpg-frow { margin-bottom: 14px; }
.mtpg-frow:last-child { margin-bottom: 0; }
.mtpg-finput {
  width: 100%; height: 34px; padding: 0 10px;
  border: 1px solid var(--mtpg-border-2);
  border-radius: var(--mtpg-radius-md);
  font-family: inherit; font-size: 13px;
  color: var(--mtpg-text-1); background: #fff;
}
.mtpg-finput:focus {
  outline: none; border-color: var(--mtpg-primary);
  box-shadow: 0 0 0 3px var(--mtpg-primary-soft);
}
.mtpg-ftextarea {
  width: 100%; min-height: 76px; padding: 8px 10px; resize: vertical;
  border: 1px solid var(--mtpg-border-2); border-radius: var(--mtpg-radius-md);
  font-family: inherit; font-size: 13px; color: var(--mtpg-text-1);
  background: #fff; line-height: 1.5;
}
.mtpg-ftextarea:focus {
  outline: none; border-color: var(--mtpg-primary);
  box-shadow: 0 0 0 3px var(--mtpg-primary-soft);
}

/* Mini spinner */
.mtpg-spinner {
  width: 14px; height: 14px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: mtpg-spin 0.7s linear infinite;
  display: inline-block;
}
@keyframes mtpg-spin { to { transform: rotate(360deg); } }
`;

// ----------------------------------------
// Small inline icons
// ----------------------------------------
function IconPlus({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
function IconClose({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
function IconEdit({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M11.3 2.7a1.6 1.6 0 012.3 2.3l-7.6 7.6-3 .7.7-3 7.6-7.6z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function IconTrash({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M2.5 4.5h11M6 4.5V3a1 1 0 011-1h2a1 1 0 011 1v1.5M4 4.5l.6 8.1a1 1 0 001 .9h4.8a1 1 0 001-.9l.6-8.1M6.5 7v4M9.5 7v4"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function IconUsers({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden>
      <circle cx="7.5" cy="6.5" r="2.6" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M2.8 15.5c.5-2.4 2.4-3.8 4.7-3.8s4.2 1.4 4.7 3.8"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <path
        d="M13.2 9a2.3 2.3 0 100-4.4M15 12.1c1.3.4 2.2 1.4 2.5 2.9"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function ManageTillUserPage() {
  const [tillUsers, setTillUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");

  // Filter and pagination state
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1); // Changed to 1-indexed page number
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // Create Till User Dialog State
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [newTillUserName, setNewTillUserName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [createLoading, setCreateLoading] = useState(false);

  // Edit Till User Dialog State
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editTillUserId, setEditTillUserId] = useState(null);
  const [editTillUserName, setEditTillUserName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editLoading, setEditLoading] = useState(false);

  const fetchTillUsers = async () => {
    setLoading(true);
    try {
      const res = await API.get("/tilluser", {
        params: {
          search,
          page, // now 1-indexed as expected by your backend
          limit: rowsPerPage,
        },
      });
      setTillUsers(res.data.tillUsers || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      setAlertMessage(error.response?.data?.message || "Error fetching till users.");
      setAlertSeverity("error");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTillUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page, rowsPerPage]);

  // Handlers for Create Dialog
  const handleOpenCreateDialog = () => {
    setNewTillUserName("");
    setNewDescription("");
    setOpenCreateDialog(true);
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
  };

  const handleCreateTillUser = async () => {
    if (!newTillUserName) {
      setAlertMessage("Till user name is required.");
      setAlertSeverity("error");
      return;
    }
    setCreateLoading(true);
    try {
      await API.post("/tilluser", {
        name: newTillUserName,
        description: newDescription,
      });
      setAlertMessage("Till user created successfully!");
      setAlertSeverity("success");
      setOpenCreateDialog(false);
      fetchTillUsers();
    } catch (error) {
      setAlertMessage(error.response?.data?.message || "Error creating till user.");
      setAlertSeverity("error");
    }
    setCreateLoading(false);
  };

  // Handlers for Edit Dialog
  const handleOpenEditDialog = (user) => {
    setEditTillUserId(user._id);
    setEditTillUserName(user.name);
    setEditDescription(user.description);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
  };

  const handleEditTillUser = async () => {
    if (!editTillUserName) {
      setAlertMessage("Till user name is required.");
      setAlertSeverity("error");
      return;
    }
    setEditLoading(true);
    try {
      await API.put(`/tilluser/${editTillUserId}`, {
        name: editTillUserName,
        description: editDescription,
      });
      setAlertMessage("Till user updated successfully!");
      setAlertSeverity("success");
      setOpenEditDialog(false);
      fetchTillUsers();
    } catch (error) {
      setAlertMessage(error.response?.data?.message || "Error updating till user.");
      setAlertSeverity("error");
    }
    setEditLoading(false);
  };

  // Handler for Delete
  const handleDeleteTillUser = async (id) => {
    if (window.confirm("Are you sure you want to delete this till user?")) {
      try {
        await API.delete(`/tilluser/${id}`);
        setAlertMessage("Till user deleted successfully!");
        setAlertSeverity("success");
        fetchTillUsers();
      } catch (error) {
        setAlertMessage(error.response?.data?.message || "Error deleting till user.");
        setAlertSeverity("error");
      }
    }
  };

  // Pagination handlers for the custom component are handled by MyPaginationComponent
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newRows) => {
    setRowsPerPage(parseInt(newRows, 10));
    setPage(1);
  };

  const handleCloseSnackbar = () => setAlertMessage("");

  return (
    <div className="mtpg-root">
      <style>{MANAGE_TILL_USER_CSS}</style>

      {/* Sticky page-top */}
      <div className="mtpg-sticky-top">
        <div className="mtpg-header">
          <div className="mtpg-title-block">
            <h1 className="mtpg-title">Manage Till Users</h1>
            <div className="mtpg-subtitle">
              <span>Till operators available when recording shifts</span>
              <span className="mtpg-subtitle-dot" />
              <span className="mtpg-subtitle-count">
                <b>{total}</b> total
              </span>
            </div>
          </div>

          {/* Action bar */}
          <div className="mtpg-actions">
            <button
              type="button"
              className="mtpg-btn mtpg-btn-primary"
              onClick={handleOpenCreateDialog}
            >
              <IconPlus /> Create Till User
            </button>
          </div>
        </div>

        {/* Search bar */}
        <div className="mtpg-filterbar">
          <div className="mtpg-filter-field">
            <label htmlFor="mtpg-search">Search</label>
            <input
              id="mtpg-search"
              type="text"
              className="mtpg-input mtpg-input-search"
              placeholder="Search till users"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1); // Reset to first page on search change
              }}
            />
          </div>
          <div className="mtpg-filter-meta">
            Showing <b>{tillUsers.length}</b> of <b>{total}</b>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="mtpg-body">
        {loading ? (
          <div className="mtpg-card">
            <div className="mtpg-state-loading">
              <CircularProgress size={26} />
              <span>Loading till users…</span>
            </div>
          </div>
        ) : (
          <div className="mtpg-card">
            {tillUsers.length > 0 ? (
              <div className="mtpg-table-wrap">
                <table className="mtpg-table">
                  <thead>
                    <tr>
                      <th>Till User Name</th>
                      <th>Description</th>
                      <th>Created</th>
                      <th className="r">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tillUsers.map((user) => (
                      <tr key={user._id}>
                        <td>
                          <div className="mtpg-name-cell">
                            <span className="mtpg-name-avatar">
                              {(user.name || "?").slice(0, 1)}
                            </span>
                            <span className="mtpg-name-text">{user.name}</span>
                          </div>
                        </td>
                        <td>
                          {user.description ? (
                            <span className="mtpg-desc">{user.description}</span>
                          ) : (
                            <span className="mtpg-desc mtpg-desc-empty">
                              No description
                            </span>
                          )}
                        </td>
                        <td className="mono">
                          {formatDate(user.createdAt, "dd MMM yyyy") || "—"}
                        </td>
                        <td>
                          <div className="mtpg-row-actions">
                            <button
                              type="button"
                              className="mtpg-icon-btn"
                              onClick={() => handleOpenEditDialog(user)}
                              aria-label="Edit till user"
                              title="Edit"
                            >
                              <IconEdit />
                            </button>
                            <button
                              type="button"
                              className="mtpg-icon-btn mtpg-icon-btn-danger"
                              onClick={() => handleDeleteTillUser(user._id)}
                              aria-label="Delete till user"
                              title="Delete"
                            >
                              <IconTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="mtpg-state">
                <div className="mtpg-state-icon">
                  <IconUsers />
                </div>
                <div className="mtpg-state-title">No till users found.</div>
                <div className="mtpg-state-sub">
                  {search
                    ? `Nothing matches "${search}". Try a different search.`
                    : "Create your first till user to assign them on shifts."}
                </div>
              </div>
            )}

            {/* Custom Pagination Component */}
            <div className="mtpg-pagination-wrap">
              <MyPaginationComponent
                page={page}
                rowsPerPage={rowsPerPage}
                totalCount={total}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </div>
          </div>
        )}
      </div>

      {/* Snackbar feedback */}
      <Snackbar
        open={Boolean(alertMessage)}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={alertSeverity}
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>

      {/* Create Till User Dialog */}
      <Dialog
        open={openCreateDialog}
        onClose={handleCloseCreateDialog}
        fullWidth
        maxWidth="sm"
        className="mtpg-dlg"
        PaperProps={{ className: "mtpg-root" }}
      >
        <style>{MANAGE_TILL_USER_CSS}</style>
        <div className="mtpg-dlg-head">
          <div>
            <div className="mtpg-dlg-title">Create Till User</div>
            <div className="mtpg-dlg-sub">
              Add a till operator that can be assigned on shifts.
            </div>
          </div>
          <button
            type="button"
            className="mtpg-dlg-close"
            onClick={handleCloseCreateDialog}
            aria-label="Close"
          >
            <IconClose />
          </button>
        </div>
        <DialogContent className="mtpg-dlg-body">
          <div className="mtpg-frow">
            <label className="mtpg-flab mtpg-flab-req" htmlFor="mtpg-new-name">
              Till User Name
            </label>
            <input
              id="mtpg-new-name"
              type="text"
              className="mtpg-finput"
              placeholder="e.g. Front till — Sarah"
              value={newTillUserName}
              onChange={(e) => setNewTillUserName(e.target.value)}
            />
          </div>
          <div className="mtpg-frow">
            <label className="mtpg-flab" htmlFor="mtpg-new-desc">
              Description
            </label>
            <textarea
              id="mtpg-new-desc"
              className="mtpg-ftextarea"
              rows={3}
              placeholder="Optional notes about this till user"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
            />
            <div className="mtpg-fhint">Optional — shown in the list.</div>
          </div>
        </DialogContent>
        <div className="mtpg-dlg-foot">
          <div className="mtpg-dlg-foot-l">Name is required.</div>
          <div className="mtpg-dlg-foot-r">
            <button
              type="button"
              className="mtpg-btn mtpg-btn-ghost"
              onClick={handleCloseCreateDialog}
            >
              Cancel
            </button>
            <button
              type="button"
              className="mtpg-btn mtpg-btn-primary"
              onClick={handleCreateTillUser}
              disabled={createLoading}
            >
              {createLoading ? (
                <>
                  <span className="mtpg-spinner" /> Creating…
                </>
              ) : (
                "Create"
              )}
            </button>
          </div>
        </div>
      </Dialog>

      {/* Edit Till User Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        fullWidth
        maxWidth="sm"
        className="mtpg-dlg"
        PaperProps={{ className: "mtpg-root" }}
      >
        <style>{MANAGE_TILL_USER_CSS}</style>
        <div className="mtpg-dlg-head">
          <div>
            <div className="mtpg-dlg-title">Edit Till User</div>
            <div className="mtpg-dlg-sub">Update this till user's details.</div>
          </div>
          <button
            type="button"
            className="mtpg-dlg-close"
            onClick={handleCloseEditDialog}
            aria-label="Close"
          >
            <IconClose />
          </button>
        </div>
        <DialogContent className="mtpg-dlg-body">
          <div className="mtpg-frow">
            <label className="mtpg-flab mtpg-flab-req" htmlFor="mtpg-edit-name">
              Till User Name
            </label>
            <input
              id="mtpg-edit-name"
              type="text"
              className="mtpg-finput"
              value={editTillUserName}
              onChange={(e) => setEditTillUserName(e.target.value)}
            />
          </div>
          <div className="mtpg-frow">
            <label className="mtpg-flab" htmlFor="mtpg-edit-desc">
              Description
            </label>
            <textarea
              id="mtpg-edit-desc"
              className="mtpg-ftextarea"
              rows={3}
              placeholder="Optional notes about this till user"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
            />
            <div className="mtpg-fhint">Optional — shown in the list.</div>
          </div>
        </DialogContent>
        <div className="mtpg-dlg-foot">
          <div className="mtpg-dlg-foot-l">Name is required.</div>
          <div className="mtpg-dlg-foot-r">
            <button
              type="button"
              className="mtpg-btn mtpg-btn-ghost"
              onClick={handleCloseEditDialog}
            >
              Cancel
            </button>
            <button
              type="button"
              className="mtpg-btn mtpg-btn-primary"
              onClick={handleEditTillUser}
              disabled={editLoading}
            >
              {editLoading ? (
                <>
                  <span className="mtpg-spinner" /> Saving…
                </>
              ) : (
                "Save"
              )}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
